import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isUiEventsEnabled, onUiEventsEnabled, track, type UiContext } from '@/common/analytics';
import { createSeenSet, type UiTarget } from '@/common/uiEventQueue';
import { UI_ACTION_ATTR, UI_KIND, UI_POS_ATTR, UI_TARGET_ATTR } from '@/common/uiEventNames';
import { SOFT_REFRESH_EVENT } from '@/components/common/PullToRefresh';

/**
 * 노출·클릭 공용 관찰 — docs/unified-log-plan.md 3.2.
 *
 * 화면은 목록·섹션 컨테이너에 [useUiScope] 를 걸고, 카드 루트에 `uiTargetAttrs(kind, id, pos)` 속성만 붙인다.
 * - **노출**: 화면 전체에서 IntersectionObserver **하나**를 공유한다. 카드 면적 50% 이상이 0.5초 이상 이어서 보이면 1번.
 *   숨은 탭·백그라운드에서는 세지 않는다. 같은 목록 표시(listKey) 안의 같은 대상은 한 번만 — 뒤로가기로 목록이 복원되면
 *   같은 listKey(같은 방문 기록 칸)라서 다시 세지 않는다. 당겨서 새로고침·조건 변경은 새 listKey.
 * - **클릭**: 컨테이너에 캡처 단계 클릭 리스너 하나 — 누른 곳에서 가장 가까운 카드(`data-ui-target`)를 기록한다.
 *   카드가 아니라 `data-ui-action`(예: 섹션 "더보기")이면 섹션 대상으로 action 과 함께 기록한다.
 * 카드 컴포넌트에 ref 를 넘길 필요가 없고(속성만), 기록이 꺼져 있으면 track.* 가 아무것도 하지 않는다.
 */

const VISIBLE_RATIO = 0.5;
/** 범위 컨테이너 표시 — 범위가 겹치면 가장 안쪽 범위가 센다 */
const SCOPE_ATTR = 'data-ui-scope';
const DWELL_MS = 500;

interface Registration {
    scope: { current: UiContext };
    target: UiTarget;
    timer: number | null;
}

const registrations = new WeakMap<Element, Registration>();
const seen = createSeenSet();
let observer: IntersectionObserver | null = null;

if (typeof window !== 'undefined') {
    onUiEventsEnabled(() => {
        const io = observer;
        if (!io) return;
        live.forEach((element) => {
            io.unobserve(element);
            io.observe(element);
        });
    });
}

/** 당겨서 새로고침 횟수 — 같은 방문 기록 칸이라도 새로고침하면 새 목록 표시 */
let refreshGeneration = 0;
if (typeof window !== 'undefined') {
    window.addEventListener(SOFT_REFRESH_EVENT, () => {
        refreshGeneration++;
    });
}

/** 관찰 중인 카드 전부 — 수집이 켜지는 순간 다시 걸어(이미 보이던 카드도) 세게 한다 */
const live = new Set<Element>();

const fire = (element: Element, registration: Registration): void => {
    registration.timer = null;
    // 수집이 꺼져 있으면 세지도(중복 표시도) 않는다 — 켜지면 다시 관찰한다
    if (!isUiEventsEnabled()) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    const scope = registration.scope.current;
    const key = `${scope.listKey ?? ''}|${scope.section ?? ''}|${registration.target.kind}:${registration.target.id}`;
    if (!seen.markIfNew(key)) return;
    track.impression(scope, registration.target);
    // 한 번 센 카드는 더 볼 필요가 없다(목록 표시가 바뀌면 그 범위가 다시 건다)
    observer?.unobserve(element);
};

const sharedObserver = (): IntersectionObserver | null => {
    if (observer || typeof IntersectionObserver === 'undefined') return observer;
    observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            const registration = registrations.get(entry.target);
            if (!registration) return;
            if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO) {
                if (registration.timer === null) {
                    registration.timer = window.setTimeout(() => fire(entry.target, registration), DWELL_MS);
                }
            } else if (registration.timer !== null) {
                window.clearTimeout(registration.timer);
                registration.timer = null;
            }
        });
    }, { threshold: [0, VISIBLE_RATIO] });
    return observer;
};

/** `kind:id` 표시를 대상으로 */
const parseTarget = (element: Element): UiTarget | null => {
    const raw = element.getAttribute(UI_TARGET_ATTR);
    if (!raw) return null;
    const index = raw.indexOf(':');
    if (index <= 0 || index === raw.length - 1) return null;
    const posRaw = element.getAttribute(UI_POS_ATTR);
    const pos = posRaw !== null && /^\d+$/.test(posRaw) ? Number(posRaw) : undefined;
    return { kind: raw.slice(0, index), id: raw.slice(index + 1), pos };
};

/** 짧은 해시(목록 조건 등을 listKey 에 넣으려고) */
const shortHash = (text: string): string => {
    let hash = 5381;
    for (let i = 0; i < text.length; i++) hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
    return (hash >>> 0).toString(36);
};

/**
 * 목록 한 번 표시의 id — 방문 기록 칸(location.key) + 목록 구분(조건·검색어 등) + 새로고침 횟수.
 * 뒤로가기 복원은 같은 칸이라 같은 값, 새로 들어오거나 조건을 바꾸면 새 값.
 *
 * @param listId 화면 안에서 목록을 구분하는 값(조건이 다르면 다른 목록) — 글자 원문을 쓰더라도 해시만 나간다
 */
export function useListKey(listId: string): string {
    const location = useLocation();
    return `${location.key || 'k'}-${shortHash(listId)}-${refreshGeneration}`.slice(0, 40);
}

interface ScopeOptions {
    /** 컨테이너 자체를 섹션 노출 대상으로 센다(홈 섹션) */
    sectionTarget?: boolean;
}

/**
 * 컨테이너 안 카드의 노출·클릭을 센다. 돌려준 콜백 ref 를 목록·섹션 컨테이너에 건다
 * (조건부로 그려지는 컨테이너도 나타날 때 붙고 사라질 때 풀린다).
 * 범위가 겹치면(목록 안 "오늘의 채용") 가장 안쪽 범위가 그 카드를 센다.
 *
 * @param context 화면·섹션·목록 표시 — 바뀌어도 관찰은 다시 걸지 않고 최신 값을 쓴다
 */
export function useUiScope(context: UiContext, options: ScopeOptions = {}): (element: HTMLElement | null) => void {
    const scope = useRef<UiContext>(context);
    scope.current = context;
    const sectionTarget = options.sectionTarget === true;
    const mineRef = useRef<Set<Element>>(new Set());
    const detachRef = useRef<(() => void) | null>(null);

    // 목록 표시가 바뀌면(조건 변경·새 응답) 이미 센 카드도 다시 관찰한다 — 같은 DOM 이 재사용돼도 새 표시로 센다
    useEffect(() => {
        const io = sharedObserver();
        mineRef.current.forEach((element) => io?.observe(element));
    }, [context.listKey]);

    // 화면을 떠날 때 확실히 푼다
    useEffect(() => () => {
        detachRef.current?.();
        detachRef.current = null;
    }, []);

    return useCallback((container: HTMLElement | null): void => {
        detachRef.current?.();
        detachRef.current = null;
        if (container) detachRef.current = attach(container, scope, mineRef.current, sectionTarget);
    }, [sectionTarget]);
}

/** 컨테이너에 관찰·클릭 리스너를 붙이고, 푸는 함수를 돌려준다 */
function attach(container: HTMLElement, scope: { current: UiContext }, mine: Set<Element>, sectionTarget: boolean): () => void {
    const io = sharedObserver();
    let alive = true;

    const register = (element: Element, target: UiTarget): void => {
        if (mine.has(element)) return;
        mine.add(element);
        live.add(element);
        registrations.set(element, { scope, target, timer: null });
        io?.observe(element);
    };
    const release = (element: Element): void => {
        const registration = registrations.get(element);
        if (registration?.timer != null) window.clearTimeout(registration.timer);
        io?.unobserve(element);
        if (registration?.scope === scope) {
            registrations.delete(element);
            live.delete(element);
        }
        mine.delete(element);
    };
    /** 이 컨테이너가 직접 맡은 카드인지 — 안에 다른 범위가 있으면 그쪽이 센다 */
    const owns = (element: Element): boolean => element.closest(`[${SCOPE_ATTR}]`) === container;
    const scan = (): void => {
        if (!alive) return;
        let index = 0;
        container.querySelectorAll(`[${UI_TARGET_ATTR}]`).forEach((element) => {
            if (!owns(element)) return;
            const target = parseTarget(element);
            if (!target) return;
            // 순번을 따로 안 붙인 카드는 이 범위 안 화면 순서(0부터) — PC·폰 짝으로 그려 두고 하나를 숨기는 카드는 보이는 쪽만 센다
            const shown = element.getClientRects().length > 0;
            register(element, target.pos === undefined && shown ? { ...target, pos: index } : target);
            if (shown) index++;
        });
        // 사라진 카드·안쪽 범위로 넘어간 카드는 관찰을 푼다
        mine.forEach((element) => {
            if (element === container) return;
            if (!container.contains(element) || !owns(element)) release(element);
        });
    };

    container.setAttribute(SCOPE_ATTR, '');
    if (sectionTarget && scope.current.section) {
        register(container, { kind: UI_KIND.SECTION, id: scope.current.section });
    }
    // 같은 커밋에서 붙는 안쪽 범위가 표시를 단 뒤에 훑는다
    queueMicrotask(scan);
    const mutations = typeof MutationObserver !== 'undefined' ? new MutationObserver(scan) : null;
    mutations?.observe(container, { childList: true, subtree: true });

    const onClick = (event: MouseEvent): void => {
        const origin = event.target instanceof Element ? event.target : null;
        if (!origin) return;
        const card = origin.closest(`[${UI_TARGET_ATTR}]`);
        if (card && container.contains(card)) {
            if (!owns(card)) return;
            const registration = registrations.get(card);
            const target = registration?.scope === scope ? registration.target : parseTarget(card);
            if (target) track.click(scope.current, target);
            return;
        }
        const action = origin.closest(`[${UI_ACTION_ATTR}]`);
        if (action && container.contains(action) && owns(action) && scope.current.section) {
            track.click(scope.current, { kind: UI_KIND.SECTION, id: scope.current.section }, { action: action.getAttribute(UI_ACTION_ATTR) ?? '' });
        }
    };
    container.addEventListener('click', onClick, true);

    return () => {
        alive = false;
        container.removeAttribute(SCOPE_ATTR);
        container.removeEventListener('click', onClick, true);
        mutations?.disconnect();
        Array.from(mine).forEach(release);
    };
}
